/**
 * @file
 * Connect natural content edges to a shared footer without stretching content.
 *
 * Opt-in contract (repository root: adocs-documentation/04-Theme/Footer-Anbindung.md):
 * - .footer-boundary supplies the CSS-only minimum clearance.
 * - data-footer-boundary selects the footer line within .layout-container.
 * - data-footer-canvas may select an ancestor canvas for an embedded component.
 * - data-footer-edge marks each intrinsic content group, not its background.
 *   data-footer-edges can select existing groups instead; data-footer-spaced-edges
 *   selects borderless groups that always require clearance when terminal.
 * - .footer-edge--connected hides only that group's closing line in its CSS.
 *   The state MUST NOT change box dimensions (use transparent borders).
 */
((Drupal, once) => {
  "use strict";

  const SELECTOR = "[data-footer-boundary]";
  const SPACE = "--footer-boundary-space";
  const CONNECTED = "footer-edge--connected";
  // Allow only subpixel rounding at a shared boundary, not a visible gap.
  const EPSILON = 0.5;
  const cleanups = new WeakMap();

  function install(marker) {
    // Embedded Views cannot annotate their surrounding region. Let one marker
    // opt that ancestor in, so sibling columns participate in the same decision.
    // CSS must reserve fallback clearance on this canvas, not on the marker.
    const container = marker.dataset.footerCanvas
      ? marker.closest(marker.dataset.footerCanvas)
      : marker;
    if (!container) return;
    const scope = container.closest(".layout-container");
    const target = scope?.querySelector(marker.dataset.footerBoundary);
    if (!target) return;

    let edges = new Set();
    let frame = null;
    let disposed = false;

    function restoreFallback() {
      container.style.removeProperty(SPACE);
      edges.forEach((edge) => edge.classList.remove(CONNECTED));
    }

    function synchronizeEdges() {
      const next = new Set(
        [...container.querySelectorAll(marker.dataset.footerEdges || "[data-footer-edge]")].filter(
          // Include siblings of an embedded marker, but never claim edges that
          // belong to another independently initialized footer boundary.
          (edge) => edge.closest(SELECTOR) === marker ||
            (container !== marker && !edge.closest(SELECTOR)),
        ),
      );
      edges.forEach((edge) => {
        if (!next.has(edge)) {
          resizeObserver.unobserve(edge);
          edge.classList.remove(CONNECTED);
        }
      });
      next.forEach((edge) => {
        if (!edges.has(edge)) resizeObserver.observe(edge);
      });
      edges = next;
    }

    function update() {
      frame = null;
      if (disposed) return;
      if (!marker.isConnected || !container.isConnected || !target.isConnected) {
        restoreFallback();
        return;
      }

      // CSS owns the phone breakpoint and reserves the full footer clearance.
      // Read its mode before any style writes; there are no shared edges to
      // measure in this mode. Restore desktop state when crossing into it.
      if (getComputedStyle(container).getPropertyValue("--footer-boundary-measure").trim() === "0") {
        restoreFallback();
        return;
      }
      synchronizeEdges();

      // Resolve the responsive CSS token to pixels through the real fallback.
      // Then measure the layout with no added clearance. These synchronous
      // reads/writes finish in one animation frame; no intermediate state paints.
      // Removing our previous reservation is essential: a short page's flex
      // space may absorb padding, so subtracting the old value is not reliable.
      container.style.removeProperty(SPACE);
      const minimumGap = parseFloat(getComputedStyle(container).paddingBottom);
      if (!Number.isFinite(minimumGap)) {
        restoreFallback();
        return;
      }
      container.style.setProperty(SPACE, "0px");

      const footerTop = target.getBoundingClientRect().top;
      const canvasBottom = container.getBoundingClientRect().bottom;
      const candidates = [...edges]
        .filter((edge) => edge.getClientRects().length)
        .map((edge) => ({
          edge,
          rect: edge.getBoundingClientRect(),
          spaced: !!marker.dataset.footerSpacedEdges && edge.matches(marker.dataset.footerSpacedEdges),
        }));
      // A later row may replace an earlier edge in the same horizontal span.
      // Keep partially exposed edges, and merge coverage from multiple later
      // groups. This also handles related fields stacking on smaller screens.
      const measurements = candidates.filter(({ rect }) => {
        const coverage = candidates
          .filter((other) => other.rect.top >= rect.bottom - EPSILON && other.rect.bottom > rect.bottom + EPSILON)
          .map((other) => [Math.max(rect.left, other.rect.left), Math.min(rect.right, other.rect.right)])
          .filter(([left, right]) => right > left)
          .sort((a, b) => a[0] - b[0]);
        let coveredUntil = rect.left;
        for (const [left, right] of coverage) {
          if (left > coveredUntil + EPSILON) break;
          coveredUntil = Math.max(coveredUntil, right);
        }
        return coveredUntil < rect.right - EPSILON;
      }).map(({ edge, rect, spaced }) => ({ edge, spaced, gap: footerTop - rect.bottom }));

      // Do not claim a shared boundary if another component sits between the
      // canvas and footer, or if overflowing content reaches past the footer.
      if (
        !measurements.length ||
        Math.abs(footerTop - canvasBottom) > EPSILON ||
        measurements.some(({ gap }) => gap < -EPSILON)
      ) {
        restoreFallback();
        return;
      }

      // Each natural edge must either touch or have at least the minimum gap.
      // If two columns differ by only 20px with an 80px minimum, moving the
      // footer just 60px creates a new too-small gap under the longer column.
      // Reserve the full minimum below the natural layout instead. CSS still
      // owns viewport filling, so short pages may absorb part of this padding.
      const needsClearance = measurements.some(
        ({ gap, spaced }) => (spaced || gap > EPSILON) && gap < minimumGap - EPSILON,
      );
      container.style.setProperty(SPACE, needsClearance ? `${minimumGap}px` : "0px");
      const visibleEdges = new Set(measurements.map(({ edge }) => edge));
      edges.forEach((edge) => {
        if (!visibleEdges.has(edge)) edge.classList.remove(CONNECTED);
      });
      measurements.forEach(({ edge, gap, spaced }) => {
        edge.classList.toggle(CONNECTED, !spaced && !needsClearance && Math.abs(gap) <= EPSILON);
      });
    }

    function scheduleUpdate() {
      if (!disposed && frame === null) frame = requestAnimationFrame(update);
    }

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    // Includes intrinsic content changes, fonts/images, header/footer resizing,
    // and viewport filling. There is no scroll handler or continuous polling.
    [container, scope, target].forEach((element) => resizeObserver.observe(element));
    const mutationObserver = new MutationObserver(scheduleUpdate);
    // Ignore our own class/style writes. Drupal AJAX and grid filler changes
    // are child mutations; text replacements may instead be character data.
    mutationObserver.observe(container, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", scheduleUpdate);
    scheduleUpdate();

    cleanups.set(marker, () => {
      disposed = true;
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      if (frame !== null) cancelAnimationFrame(frame);
      restoreFallback();
    });
  }

  function boundaries(context) {
    const elements = [...context.querySelectorAll(SELECTOR)];
    if (context.matches?.(SELECTOR)) elements.unshift(context);
    return elements;
  }

  Drupal.behaviors.footerBoundary = {
    attach(context) {
      // A missing observer leaves the complete CSS-only layout in place.
      if (!("ResizeObserver" in window)) return;
      once("footer-boundary", boundaries(context)).forEach(install);
    },
    detach(context, settings, trigger) {
      if (trigger !== "unload") return;
      once.remove("footer-boundary", boundaries(context)).forEach((marker) => {
        cleanups.get(marker)?.();
        cleanups.delete(marker);
      });
    },
  };
})(Drupal, once);
